import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorItemProps } from "@/v2-backoffice-client/components/selector"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { describeMergedInto } from "@/v2-backoffice-client/customer/merge/customer.merge.utils"
import {
  countOwnersOutsideUserTeams,
  formatCountOfTotal,
} from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { useUserCustomerUpdateManyMutation } from "@/v2-backoffice-client/user/customer/update/many/user.customer.update.many.mutation"

export type UserCustomerUpdateManySelectorItemProps = Omit<
  SelectorItemProps,
  "value" | "children" | "onSelect" | "warning"
> & {
  customer: CustomerFragment
  users: UserFragment[]
  onSelect?(): void
}

/**
 * One candidate owner for the selected users. Says how many of them it
 * owns today, and takes a confirmation when some of them have no team
 * under it (the cross-customer case).
 */
function UserCustomerUpdateManySelectorItem(
  props: UserCustomerUpdateManySelectorItemProps,
) {
  const { customer, users, onSelect, ...selectorItemProps } = props

  // Reported as done only once the write is through: the caller clears
  // the selection on it, which must not happen on a failed write.
  const [updateMany, { loading, error }] = useUserCustomerUpdateManyMutation({
    onCompleted(data) {
      // errorPolicy is "all" app-wide: a rejected write resolves with the
      // error and no data instead of throwing.
      if (data?.setManyUsersCustomer) onSelect?.()
    },
  })
  errorToast(error, { title: "Failed to update the users' customer" })

  const isLinked = Boolean(customer.ExternalCustomerId)
  const mergedAway = describeMergedInto(customer)
  const ownedCount = users.filter(
    (user) => user.CustomerId === customer.Id,
  ).length
  const outsideCount = countOwnersOutsideUserTeams(users, customer)

  function describe() {
    if (mergedAway) return mergedAway
    if (!isLinked) {
      return "Not linked to HubSpot — link a company before it can own users"
    }
    if (ownedCount > 0) {
      return `Owns ${formatCountOfTotal(ownedCount, users.length, "selected user")} today`
    }
    return customer.ExternalCustomerId
  }

  return (
    <Selector.Item
      isLoading={loading}
      value={String(customer.Id)}
      disabled={!isLinked || Boolean(mergedAway)}
      warning={
        outsideCount > 0
          ? `${formatCountOfTotal(outsideCount, users.length, "selected user")} ${outsideCount === 1 ? "has" : "have"} no team under this customer.`
          : undefined
      }
      confirmContent="Update anyway?"
      onSelect={() => {
        updateMany({
          variables: {
            ids: users.map((user) => user.Id),
            customerId: customer.Id,
          },
        })
      }}
      {...selectorItemProps}
    >
      <Selector.ItemText
        primary={customer.Name ?? "Unnamed"}
        secondary={describe()}
      />
    </Selector.Item>
  )
}

export default UserCustomerUpdateManySelectorItem
